import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { userAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';

const FriendContext = createContext();

export const useFriend = () => {
  const context = useContext(FriendContext);
  if (!context) {
    throw new Error('useFriend must be used within a FriendProvider');
  }
  return context;
};

export const FriendProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [friendRequests, setFriendRequests] = useState([]);
  const [sentFriendRequests, setSentFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);

  // Friend status for specific profiles (cache)
  const [friendStatuses, setFriendStatuses] = useState(new Map());

  // Load user's friends
  const loadFriends = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const friendsResponse = await userAPI.getUserFriends(user.id);
      if (friendsResponse?.success) {
        setFriends(friendsResponse.friends || []);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load friend requests
  const loadFriendRequests = useCallback(async () => {
    if (!user) return;

    try {
      const { friendsAPI } = await import('../services/api');
      const requestsResponse = await friendsAPI.getRequests();
      if (requestsResponse?.success) {
        setFriendRequests(requestsResponse.received_requests || []);
        setSentFriendRequests(requestsResponse.sent_requests || []);
      }
    } catch (error) {
      console.error('Failed to load friend requests:', error);
    }
  }, [user]);

  // Send friend request
  const sendFriendRequest = async (friendId, friendName) => {
    try {
      const response = await userAPI.sendFriendRequest(friendId);
      if (response?.success) {
        // Update local friend status
        setFriendStatuses(prev => new Map(prev.set(friendId, 'pending')));

        toast.success(`Friend request sent to ${friendName || 'user'}! 👥`);
        return true;
      }
    } catch (error) {
      console.error('Failed to send friend request:', error);
      toast.error('Failed to send friend request');
    }
    return false;
  };

  // Remove friend
  const removeFriend = async (friendId, friendName) => {
    try {
      const response = await userAPI.removeFriend(friendId);
      if (response?.success) {
        // Update local friend status
        setFriendStatuses(prev => {
          const newMap = new Map(prev);
          newMap.delete(friendId);
          return newMap;
        });

        // Remove from friends list
        setFriends(prev => prev.filter(friend => friend.id !== friendId));

        // Reload friends list to ensure consistency
        loadFriends();

        toast.success(`Removed ${friendName || 'user'} from friends`);
        return true;
      }
    } catch (error) {
      console.error('Failed to remove friend:', error);
      toast.error('Failed to remove friend');
    }
    return false;
  };

  // Get friend status for a specific user
  const getFriendStatus = (userId) => {
    return friendStatuses.get(userId) || 'none';
  };

  // Set friend status for a specific user
  const setFriendStatus = (userId, status) => {
    setFriendStatuses(prev => new Map(prev.set(userId, status)));
  };

  // Load friend status from API (for profile pages)
  const loadFriendStatus = async (profileId) => {
    if (!user || !profileId) return 'none';

    try {
      // This would be a new API endpoint to get friend status for specific profile
      // For now, we'll use the existing profile API that includes friend_status
      // This is called from ProfileDetail component
      return 'none'; // Placeholder - actual implementation in ProfileDetail
    } catch (error) {
      console.error('Failed to load friend status:', error);
      return 'none';
    }
  };

  // Accept friend request
  const acceptFriendRequest = async (userId) => {
    try {
      const { friendsAPI } = await import('../services/api');
      const response = await friendsAPI.acceptRequest(userId);
      if (response?.success) {
        // Update friend status
        setFriendStatus(userId, 'accepted');

        // Remove from friend requests list
        setFriendRequests(prev => prev.filter(request => request.id !== userId));

        toast.success('Friend request accepted!');

        // Reload friends list
        loadFriends();
        return true;
      }
    } catch (error) {
      console.error('Failed to accept friend request:', error);
      toast.error('Failed to accept friend request');
    }
    return false;
  };

  // Decline friend request
  const declineFriendRequest = async (userId) => {
    try {
      const { friendsAPI } = await import('../services/api');
      const response = await friendsAPI.cancelRequest(userId);
      if (response?.success) {
        // Remove from friend requests list
        setFriendRequests(prev => prev.filter(request => request.id !== userId));

        toast.success('Friend request declined!');
        return true;
      }
    } catch (error) {
      console.error('Failed to decline friend request:', error);
      toast.error('Failed to decline friend request');
    }
    return false;
  };

  // Load friends and friend requests on mount
  useEffect(() => {
    if (user && !authLoading) {
      loadFriends();
      loadFriendRequests();
    }
  }, [user, authLoading, loadFriends, loadFriendRequests]);

  const value = {
    // State
    friendRequests,
    sentFriendRequests,
    friends,
    loading,
    friendStatuses,

    // Actions
    loadFriends,
    loadFriendRequests,
    sendFriendRequest,
    removeFriend,
    acceptFriendRequest,
    declineFriendRequest,
    getFriendStatus,
    setFriendStatus,
    loadFriendStatus,
  };

  return (
    <FriendContext.Provider value={value}>
      {children}
    </FriendContext.Provider>
  );
};
